/**
 * CSV import with explicit column → field mapping (admin UI).
 */

import { parseCsv } from './guestSheetCsv';
import {
  adminRsvpFromFormAndVerbal,
  FORM_RSVP,
  GUEST_POOL,
  GUEST_SIDE,
  SITE_VISIBILITY,
  VERBAL_RSVP,
  computeRsvpStatusFromForm,
  looksLikePostalAddress,
  normalizeEmail,
  normalizeCoachIn,
  normalizeCoachOut,
  normalizeGuestSide,
  normalizePlusOnes,
  normalizeTags,
} from './guestAllowlist';

export const CSV_FIELD_OPTIONS = [
  { id: '_skip', label: '— Ignore —' },
  { id: 'email', label: 'Email (optional; row still imports if empty or invalid)' },
  { id: 'nickname', label: 'Nickname (welcome / splash)' },
  { id: 'lastName', label: 'Last name' },
  { id: 'group', label: 'Group / household' },
  { id: 'guestSide', label: "Who's guest (Ben / Meg / Joint)" },
  { id: 'siteVisibility', label: 'Invite tier (ceremony+ / reception / text)' },
  { id: 'onSiteYn', label: 'On-site rooms Y/N' },
  { id: 'verbalRsvp', label: 'Verbal RSVP (yes / no)' },
  { id: 'formRsvp', label: 'Form RSVP (yes / pending / no)' },
  { id: 'pool', label: 'Pool (invited / backup)' },
  { id: 'phone', label: 'Phone / contact' },
  { id: 'address', label: 'Postal address' },
  { id: 'dietary', label: 'Dietary' },
  { id: 'accommodation', label: 'Accommodation' },
  { id: 'coachIn', label: 'Coach in (yes / empty)' },
  { id: 'coachOut', label: 'Coach out (10:30 / 12:30 / empty)' },
  { id: 'notes', label: 'Notes' },
  { id: 'plusOnes', label: 'Plus ones (extra seats)' },
  { id: 'tags', label: 'Tags / labels (comma-separated)' },
];

function normH(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

/** Guess initial mapping from header labels. */
export function guessCsvMapping(headers) {
  const n = headers.map(normH);
  const pick = (...patterns) => {
    for (const p of patterns) {
      const key = normH(p);
      const i = n.indexOf(key);
      if (i >= 0) return i;
    }
    for (let i = 0; i < n.length; i += 1) {
      for (const p of patterns) {
        if (n[i].includes(normH(p)) || normH(p).includes(n[i])) return i;
      }
    }
    return -1;
  };
  const m = {
    email: pick('email', 'e_mail', 'email_address'),
    nickname: pick('first_name', 'firstname', 'first', 'nickname', 'name', 'guest'),
    lastName: pick('last_name', 'lastname', 'surname', 'family_name'),
    group: pick('group', 'household', 'household_group', 'party'),
    guestSide: pick('side', 'side_ben_meg'),
    siteVisibility: pick('sitevisibility', 'visibility', 'invite', 'tier', 'ceremony'),
    onSiteYn: pick('penicuik', 'on_site', 'onsite', 'rooms'),
    verbalRsvp: -1,
    formRsvp: pick('rsvp', 'rsvp_status', 'form_rsvp', 'confirmed'),
    pool: pick('pool', 'backup', 'status_invited'),
    phone: pick('phone', 'mobile', 'tel'),
    address: pick('address', 'postal'),
    dietary: pick('dietary', 'diet'),
    accommodation: pick('accommodation', 'accomodation', 'lodging', 'stay'),
    coachIn: pick('coach_in', 'coachin', 'arrival_coach'),
    coachOut: pick('coach_out', 'coachout', 'return_coach', 'coach_return'),
    notes: pick('notes', 'note'),
    plusOnes: pick('plus', 'plus_one', 'plus_ones', 'extras', 'additional_guests'),
    tags: pick('tags', 'labels', 'label'),
  };
  const out = {};
  for (const opt of CSV_FIELD_OPTIONS) {
    if (opt.id === '_skip') continue;
    const v = m[opt.id];
    out[opt.id] = typeof v === 'number' && v >= 0 ? v : null;
  }
  return out;
}

export function parseCsvToTable(text) {
  const rows = parseCsv(text);
  if (rows.length < 1) return { headers: [], dataRows: [], error: 'No rows in file.' };
  const headers = rows[0].map((c) => String(c));
  const dataRows = rows
    .slice(1)
    .filter((r) => r.some((c) => String(c ?? '').trim() !== ''));
  return { headers, dataRows, error: null };
}

function getCell(row, colIdx) {
  if (colIdx == null || colIdx < 0 || colIdx >= row.length) return '';
  return String(row[colIdx] ?? '').trim();
}

function parseVerbalCell(raw) {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'yes' || v === 'y' || v === 'true' || v === '1') return VERBAL_RSVP.YES;
  if (v === 'no' || v === 'n' || v === 'false' || v === '0') return VERBAL_RSVP.NO;
  if (
    v === 'tentative' ||
    v === 'maybe' ||
    v === 'unsure' ||
    v.includes('tentative')
  ) {
    return VERBAL_RSVP.TENTATIVE;
  }
  return VERBAL_RSVP.UNSET;
}

function parseFormCell(raw) {
  const v = String(raw || '').trim().toLowerCase().replace(/\s+/g, '_');
  if (v === 'yes' || v === 'confirmed' || v === 'y' || v === 'accept' || v === 'attending') return FORM_RSVP.YES;
  if (v === 'no' || v === 'declined' || v === 'n' || v === 'decline' || v === 'unable') return FORM_RSVP.NO;
  if (v === 'pending' || v === 'awaiting' || v === 'not_sent' || v === 'unsure') return FORM_RSVP.PENDING;
  return FORM_RSVP.PENDING;
}

function parsePoolCell(raw) {
  const v = String(raw || '').trim().toLowerCase();
  if (v.includes('backup') || v === 'reserve' || v === 'b' || v === 'hold') return GUEST_POOL.BACKUP;
  return GUEST_POOL.INVITED;
}

function parseSiteVisibilityCell(raw, onSiteYnRaw) {
  const pen =
    String(onSiteYnRaw || '')
      .trim()
      .toLowerCase();
  if (pen === 'y' || pen === 'yes' || pen === 'true' || pen === '1') {
    return SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK;
  }
  const v = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (
    v.includes('penicuik') ||
    v.includes('on_site') ||
    v === 'ceremony_reception_penicuik'
  ) {
    return SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK;
  }
  if (v.includes('reception_only') || v === 'evening' || (v.includes('reception') && !v.includes('ceremony'))) {
    return SITE_VISIBILITY.RECEPTION_ONLY;
  }
  if (
    v.includes('ceremony') ||
    v === 'full' ||
    v === 'all' ||
    v === 'yes' ||
    v === 'ceremony_reception' ||
    !v
  ) {
    return SITE_VISIBILITY.CEREMONY_RECEPTION;
  }
  return SITE_VISIBILITY.CEREMONY_RECEPTION;
}

/**
 * @param {string[][]} dataRows
 * @param {Record<string, number|null|undefined>} mapping fieldId -> column index
 * @param {() => string} newGuestId
 */
export function buildGuestsFromMapping(dataRows, mapping, newGuestId) {
  const emailCol = mapping.email;

  const guests = [];
  const warnings = [];

  for (let r = 0; r < dataRows.length; r += 1) {
    const row = dataRows[r];
    const emailRaw = getCell(row, emailCol);
    const nickRaw = getCell(row, mapping.nickname);
    const lastRaw = getCell(row, mapping.lastName);
    const phoneRaw = getCell(row, mapping.phone);
    const groupRaw = getCell(row, mapping.group);
    const addrRaw = getCell(row, mapping.address);
    const notesRaw = getCell(row, mapping.notes);
    if (
      !String(emailRaw || '').trim() &&
      !String(nickRaw || '').trim() &&
      !String(lastRaw || '').trim() &&
      !String(phoneRaw || '').trim() &&
      !String(groupRaw || '').trim() &&
      !String(addrRaw || '').trim() &&
      !String(notesRaw || '').trim()
    ) {
      continue;
    }

    const email = normalizeEmail(emailRaw);

    const nickname = nickRaw || lastRaw || 'Guest';
    const lastName = lastRaw || '';
    let group = getCell(row, mapping.group) || '';
    const sideRaw = getCell(row, mapping.guestSide);
    const guestSide = normalizeGuestSide(sideRaw);
    const siteVisibility = parseSiteVisibilityCell(
      getCell(row, mapping.siteVisibility),
      getCell(row, mapping.onSiteYn),
    );
    const verbalRsvp = parseVerbalCell(getCell(row, mapping.verbalRsvp));
    const formRsvp = parseFormCell(getCell(row, mapping.formRsvp));
    const pool = parsePoolCell(getCell(row, mapping.pool));
    const phone = getCell(row, mapping.phone) || '';
    let address = getCell(row, mapping.address) || '';
    if (looksLikePostalAddress(group)) {
      if (!address.trim()) address = group;
      else if (group.trim() && address.trim() !== group.trim()) {
        address = `${address}\n${group}`.trim();
      }
      group = '';
    }
    const dietary = getCell(row, mapping.dietary) || '';
    const accommodation = getCell(row, mapping.accommodation) || '';
    const coachIn = normalizeCoachIn(getCell(row, mapping.coachIn));
    const coachOut = normalizeCoachOut(getCell(row, mapping.coachOut));
    const notes = getCell(row, mapping.notes) || '';
    const plusOnes = normalizePlusOnes(getCell(row, mapping.plusOnes));
    const tags = normalizeTags(getCell(row, mapping.tags));

    const rsvpStatus = computeRsvpStatusFromForm(formRsvp);
    const rsvpUpdatedAt =
      formRsvp !== FORM_RSVP.PENDING ? new Date().toISOString() : null;
    const adminRsvpStatus = adminRsvpFromFormAndVerbal(formRsvp, verbalRsvp, rsvpStatus);

    guests.push({
      id: newGuestId(),
      email,
      firstName: nickname,
      lastName,
      group,
      guestSide: guestSide || GUEST_SIDE.UNSPECIFIED,
      siteVisibility,
      pool,
      verbalRsvp,
      formRsvp,
      adminRsvpStatus,
      isGroupLead: false,
      sortOrder: guests.length,
      phone,
      address,
      dietary,
      accommodation,
      coachIn,
      coachOut,
      notes,
      plusOnes,
      tags,
      rsvpStatus,
      rsvpUpdatedAt,
    });
  }

  return { guests, warnings };
}
